import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";
import {
    onInstallAvailability,
    promptInstall,
    isIOS,
    isAndroid,
    isStandalone,
    wasDismissedRecently,
    markDismissed,
} from "../lib/pwa";

/**
 * Coffeein7 "Add to Home Screen" prompt.
 *
 * Behaviour:
 *   - Never shows if already installed (running in standalone mode).
 *   - Never shows if the user dismissed it in the last 14 days.
 *   - Android/Chrome: uses the native beforeinstallprompt event.
 *   - iOS Safari: shows a simple "Share → Add to Home Screen" instruction
 *     because Apple doesn't expose a programmatic install API.
 *   - Waits 6 seconds after mount so it never blocks the first paint.
 */
const InstallPrompt = () => {
    const [visible, setVisible] = useState(false);
    const [androidReady, setAndroidReady] = useState(false);
    const [showIosSheet, setShowIosSheet] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;              // already installed
        if (wasDismissedRecently()) return;      // user said "maybe later"

        // Subscribe to Chrome's beforeinstallprompt state.
        const unsub = onInstallAvailability((available) => {
            setAndroidReady(available);
            if (available) setVisible(true);
        });

        // iOS Safari never fires beforeinstallprompt — show the sheet after
        // a short delay if we can detect iOS Safari.
        let iosTimer = null;
        if (isIOS() && !isStandalone()) {
            iosTimer = setTimeout(() => setVisible(true), 6000);
        }
        return () => {
            unsub();
            if (iosTimer) clearTimeout(iosTimer);
        };
    }, []);

    const dismiss = () => {
        markDismissed();
        setVisible(false);
        setShowIosSheet(false);
    };

    const handleAdd = async () => {
        if (androidReady) {
            const { outcome } = await promptInstall();
            if (outcome === "dismissed") markDismissed();
            setVisible(false);
        } else if (isIOS()) {
            setShowIosSheet(true);
        }
    };

    if (!visible) return null;
    return (
        <>
            <AnimatePresence>
                {visible && !showIosSheet && (
                    <motion.div
                        data-testid="pwa-install-prompt"
                        initial={{ y: 120, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 120, opacity: 0 }}
                        transition={{ type: "spring", damping: 24, stiffness: 260 }}
                        className="fixed left-3 right-3 bottom-3 z-40 mx-auto max-w-md
                                   bg-[#2C1A12] text-white rounded-3xl p-4 pr-3
                                   shadow-[0_20px_50px_rgba(44,26,18,0.35)]
                                   flex items-center gap-3"
                    >
                        <div className="h-11 w-11 rounded-2xl bg-[#FF4F18] flex items-center justify-center shrink-0">
                            <Download className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-sm leading-tight">
                                Keep Coffeein7 one tap away
                            </div>
                            <div className="text-white/65 text-xs mt-0.5">
                                Add to Home Screen — no App Store required.
                            </div>
                        </div>
                        <button
                            data-testid="pwa-add-btn"
                            onClick={handleAdd}
                            className="shrink-0 rounded-full bg-white text-[#2C1A12] px-4 py-2 font-display font-bold text-sm active:scale-95"
                        >
                            Add
                        </button>
                        <button
                            data-testid="pwa-dismiss-btn"
                            onClick={dismiss}
                            aria-label="Dismiss install prompt"
                            className="shrink-0 h-9 w-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15"
                        >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* iOS Safari instructional sheet — Apple doesn't allow silent install */}
            <AnimatePresence>
                {showIosSheet && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={dismiss}
                            className="fixed inset-0 bg-[#2C1A12]/60 z-50"
                        />
                        <motion.div
                            data-testid="pwa-ios-sheet"
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 300 }}
                            className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md
                                       bg-[#F9F6F0] rounded-t-[32px] p-6 pb-8"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                        Add to Home Screen
                                    </div>
                                    <div className="font-display font-black text-2xl mt-1">
                                        Two taps in Safari
                                    </div>
                                </div>
                                <button
                                    onClick={dismiss}
                                    className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                                    aria-label="Close"
                                >
                                    <X className="h-4 w-4" strokeWidth={2.5} />
                                </button>
                            </div>
                            <ol className="mt-5 space-y-4">
                                {[
                                    { n: "1", label: "Tap the Share icon at the bottom of Safari", icon: <Share className="h-4 w-4" strokeWidth={2.5} /> },
                                    { n: "2", label: "Scroll and tap “Add to Home Screen”", icon: <Download className="h-4 w-4" strokeWidth={2.5} /> },
                                    { n: "3", label: "Open Coffeein7 from your home screen — like an app", icon: <span className="text-xs font-bold">☕</span> },
                                ].map((step) => (
                                    <li key={step.n} className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-[#FF4F18] text-white flex items-center justify-center font-display font-bold text-sm shrink-0">
                                            {step.n}
                                        </div>
                                        <div className="text-sm text-[#2C1A12] font-medium flex-1">
                                            {step.label}
                                        </div>
                                        <div className="text-[#8C7A72] shrink-0">{step.icon}</div>
                                    </li>
                                ))}
                            </ol>
                            <button
                                onClick={dismiss}
                                className="mt-6 w-full py-3 rounded-full border-2 border-[#E8DCCB] font-display font-bold text-sm active:scale-[0.98]"
                            >
                                Got it
                            </button>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default InstallPrompt;
